import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";
import {
  ArrowLeftOnRectangleIcon,
  ArrowRightOnRectangleIcon,
  ArrowRightIcon,
  BookOpenIcon,
  BriefcaseIcon,
  CalendarDaysIcon,
  HomeIcon,
  PlusCircleIcon,
  TruckIcon,
  UserGroupIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from "@heroicons/react/24/outline";
import { format, isSameDay } from "date-fns";
import { nb } from "date-fns/locale";
import RoleOnly from "../../routes/roleOnly";
import { DashboardAdminSetupAlerts } from "../../components/dashboard/companyGuide/DashboardAdminSetupAlerts";
import CurrentPhoneSection from "./phone/currentPhoneSection";
import { HourForm } from "../../components/dashboard/hours/hour_form";
import { PMDialog } from "../../components/utils/PMDialog";
import { ReportHoursTag } from "../../components/dashboard/service/reports/report-hours-tag";
import { ItemListProductStatusTag } from "../../components/dashboard/service/reports/item-list-product-status-tag";
import { fetchHours } from "../../api/hour";
import { fetchUsers } from "../../api/users";
import axios from "../../api/axios";
import { getHseDocuments } from "../../api/hse/documents";
import { toast } from "react-hot-toast";
import { eventPath, formatEventLocalTimeRange, getEventColor, getEventIcon } from "./calendar/utils";
import { ItemStatus, status } from "../../components/Utils";
import { ReportSimpleAgreementStatus } from "../../components/dashboard/service/reports/utils";
import { DashboardStatsSection } from "../../components/utils/dashboardStatsSection";
import { AccountingSummarySection } from "../../components/dashboard/accounting/AccountingSummarySection";
import { HseEventForm } from "../../components/dashboard/hse/hseEventForm";
import { fetchDrivingDriverByUser } from "../../api/driving/driver";
import { driverVehicleAction } from "./driving/drivers/utils";
import VehiclesListDialog from "./vehicles/list-dialog";
import { EmployeeToolsDialog } from "../../components/dashboard/tools/EmployeeToolsDialog";
import ReportGuideOnboardingDialog from "../../components/dashboard/service/reports/ReportGuideOnboardingDialog";
import {
  createReportGuideRun,
  fetchReportGuideTemplates,
} from "../../api/reportGuides";
import { excludingPromangStockReportGuideTemplates } from "../../utils/promangStockReportGuide";

const todayString = () => new Date().toISOString().substring(0, 10);

const moduleLabels = {
  report: "Serviceordre",
  work_order: "Arbeidsordre",
  project_plan: "Planlagt aktivitet",
  project: "Prosjekt",
  order: "Ordre",
  case: "Sak",
  absence: "Fravær",
};

const getWorkingDayInfo = (event, date) => {
  const schedule = event?.working_hours?.schedule;
  if (!Array.isArray(schedule)) return null;
  return schedule.find((entry) => entry?.date === date) || null;
};

const getEventTimeLabel = (event, date) => {
  const workingDayInfo = getWorkingDayInfo(event, date);

  if (workingDayInfo && !workingDayInfo.day_off) {
    const hours =
      workingDayInfo.hours === null || workingDayInfo.hours === undefined
        ? null
        : Number(workingDayInfo.hours);

    return `${workingDayInfo.start_time} - ${workingDayInfo.end_time}${
      Number.isFinite(hours) ? ` (${hours} t)` : ""
    }`;
  }

  if (event?.start_time && event?.end_time) {
    return formatEventLocalTimeRange(event.start_time, event.end_time);
  }

  return "";
};

const getEventMetaRows = (event) => {
  const rows = [];

  if (event?.module === "report" && event?.module_details?.report_number) {
    rows.push(`Rapport #${event.module_details.report_number}`);
  }

  if (event?.module === "work_order" && event?.module_details?.work_order_number) {
    rows.push(`Arbeidsordre #${event.module_details.work_order_number}`);
  }

  if (event?.module === "project_plan" && event?.module_details?.project_activity) {
    rows.push(
      event.module_details.project_activity.display_title ||
        event.module_details.project_activity.title
    );
  }

  if (event?.module_details?.customer?.displayTitle) {
    rows.push(event.module_details.customer.displayTitle);
  } else if (event?.module_details?.customer) {
    rows.push(event.module_details.customer);
  }

  if (event?.module_details?.project?.displayTitle) {
    rows.push(event.module_details.project.displayTitle);
  } else if (event?.module_details?.project?.name) {
    rows.push(event.module_details.project.name);
  }

  if (event?.location) {
    rows.push(event.location);
  }

  return rows.filter(Boolean);
};

const getPlannedHours = (event, date) => {
  const workingDayInfo = getWorkingDayInfo(event, date);
  if (workingDayInfo && !workingDayInfo.day_off) {
    return Number(workingDayInfo.hours) || 0;
  }

  if (event?.module_details?.total_hours !== undefined) {
    return Number(event.module_details.total_hours) || 0;
  }

  if (event?.start_time && event?.end_time) {
    const start = new Date(event.start_time);
    const end = new Date(event.end_time);
    const durationMs = end.getTime() - start.getTime();

    if (Number.isFinite(durationMs) && durationMs > 0) {
      return durationMs / (1000 * 60 * 60);
    }
  }

  return 0;
};

const getDisplayStatus = (event) => {
  if (!event) return "Planlagt";

  if (
    (event.module === "report" || event.module === "work_order") &&
    event?.module_details?.status
  ) {
    return event.module_details.status;
  }

  return event.status || "Planlagt";
};

/** Status chips matching calendar day view (`RenderDayView`): `ItemStatus` + avtale for serviceordre. */
const EventStatusChips = ({ event }) => {
  const { t } = useTranslation();
  const item = event?.module_details || event;
  const statusVal = item?.status ?? event?.status;
  const mapped =
    statusVal != null && typeof statusVal !== "object" && Boolean(status[statusVal]);
  const rawLabel = getDisplayStatus(event);
  const fallbackLabel =
    rawLabel === "Planlagt" ? t("dashboard_report.status.planned") : rawLabel;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {mapped ? (
        <ItemStatus item={item} />
      ) : (
        <span className="inline-flex rounded-full bg-slate-100 px-2 text-xs font-semibold leading-5 text-slate-700">
          {fallbackLabel}
        </span>
      )}
      {event?.module === "report" ? (
        <ReportSimpleAgreementStatus
          isApproved={event?.module_details?.appointment_confirmed}
        />
      ) : null}
    </div>
  );
};

const ActionLink = ({ to, icon: Icon, title, subtitle }) => (
  <Link
    to={to}
    className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 transition hover:border-slate-300 hover:bg-slate-50"
  >
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500">{subtitle}</div>
    </div>
  </Link>
);

const ActionButton = ({ onClick, icon: Icon, title, subtitle }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
  >
    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
      <Icon className="h-5 w-5" />
    </div>
    <div className="min-w-0">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="text-xs text-slate-500">{subtitle}</div>
    </div>
  </button>
);

const SideSection = ({ title, description, children }) => (
  <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-4">
      <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
        {title}
      </h2>
      {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
    </div>
    <div className="space-y-3">{children}</div>
  </section>
);

export const DashboardReport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const currentUser = useSelector((state) => state.session.currentUser);

  const [selectedDate, setSelectedDate] = useState(todayString());
  const [eventsState, setEventsState] = useState({ isLoading: false, data: [] });
  const [dayHours, setDayHours] = useState([]);
  const [hourFormId, setHourFormId] = useState(null);
  const [lastChangedHour, setLastChangedHour] = useState(null);
  const [prefill, setPrefill] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [colleaguesOpen, setColleaguesOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  const [vehiclesListDialogOpen, setVehiclesListDialogOpen] = useState(false);
  const [reportGuideState, setReportGuideState] = useState({
    open: false,
    template: null,
    report: null,
    isSaving: false,
  });
  const [reportGuideChooserState, setReportGuideChooserState] = useState({
    open: false,
    report: null,
    templates: [],
  });
  const [colleaguesState, setColleaguesState] = useState({
    isLoading: false,
    data: [],
  });
  const [driverState, setDriverState] = useState({
    isLoading: false,
    driver: null,
    vehicle: null,
  });
  const [handbookAckState, setHandbookAckState] = useState({
    loading: false,
    pending: 0,
    total: 0,
  });

  useEffect(() => {
    document.title = "Dashboard Report | Promang";
  }, []);

  useEffect(() => {
    if (!currentUser?.id) return;
    let cancelled = false;
    (async () => {
      setHandbookAckState((s) => ({ ...s, loading: true }));
      try {
        const [hms, ks] = await Promise.all([
          getHseDocuments({ category: "hms", status: "approved" }),
          getHseDocuments({ category: "ks", status: "approved" }),
        ]);
        const docs = [...(hms?.documents || []), ...(ks?.documents || [])];
        const total = docs.length;
        const pending = docs.filter(
          (d) => !d?.current_user_read_status?.acknowledged_read_understood
        ).length;
        if (!cancelled) {
          setHandbookAckState({ loading: false, pending, total });
        }
      } catch (_e) {
        if (!cancelled) {
          setHandbookAckState({ loading: false, pending: 0, total: 0 });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.id) return;

    const run = async () => {
      setEventsState((state) => ({ ...state, isLoading: true }));

      try {
        const params = new URLSearchParams({
          date: selectedDate,
          view: "day",
          users: currentUser.id.toString(),
        });
        const response = await axios.get(`/events?${params}`);
        const list = Array.isArray(response.data) ? response.data : [];
        setEventsState({ isLoading: false, data: list });
      } catch (_error) {
        setEventsState({ isLoading: false, data: [] });
      }
    };

    run();
  }, [selectedDate, currentUser?.id, refreshTick]);

  useEffect(() => {
    if (!currentUser?.id) return;

    const controller = {
      date: selectedDate,
      view: "day",
      date_from: selectedDate,
      date_to: selectedDate,
      filters: [{ name: "USER", value: currentUser.id }],
    };

    fetchHours(controller)
      .then((response) => setDayHours(Array.isArray(response?.data) ? response.data : []))
      .catch(() => setDayHours([]));
  }, [selectedDate, currentUser?.id, lastChangedHour?.id, refreshTick]);

  useEffect(() => {
    if (!colleaguesOpen) return;
    if (colleaguesState.data.length > 0) return;

    const run = async () => {
      setColleaguesState({ isLoading: true, data: [] });
      try {
        const response = await fetchUsers({
          filters: [{ name: "EMPLOYEE", value: true }],
          page: 1,
          per_page: 40,
          query: "",
          sort: "asc",
          sortBy: "name",
          view: "all",
          fields:
            "id,displayTitle,name,email,phone,mobile_phone,user_next_of_kins",
        });
        setColleaguesState({
          isLoading: false,
          data: Array.isArray(response?.data) ? response.data : [],
        });
      } catch (_error) {
        setColleaguesState({ isLoading: false, data: [] });
      }
    };

    run();
  }, [colleaguesOpen, colleaguesState.data.length]);

  useEffect(() => {
    if (!currentUser?.id) return;

    const run = async () => {
      setDriverState((state) => ({ ...state, isLoading: true }));
      try {
        const response = await fetchDrivingDriverByUser({
          user_id: currentUser.id,
          fields:
            "id,displayTitle,checked_into_vehicle.id,checked_into_vehicle.displayTitle,checked_into_vehicle.number_plate,checked_into_vehicle.distance_driven_today,checked_into_vehicle.distance_driven_this_week,checked_into_vehicle.distance_driven_this_month",
        });

        const driver = response?.data?.id ? response.data : null;
        setDriverState({
          isLoading: false,
          driver,
          vehicle: driver?.checked_into_vehicle || null,
        });
      } catch (_error) {
        setDriverState({
          isLoading: false,
          driver: null,
          vehicle: null,
        });
      }
    };

    run();
  }, [currentUser?.id, refreshTick]);

  const visibleEvents = (Array.isArray(eventsState.data) ? eventsState.data : [])
    .filter((event) => {
      const workingDayInfo = getWorkingDayInfo(event, selectedDate);
      return !(workingDayInfo && workingDayInfo.day_off);
    })
    .sort((left, right) => {
      const leftTime = new Date(left.start_time).getTime();
      const rightTime = new Date(right.start_time).getTime();
      return leftTime - rightTime;
    });

  const totalHoursToday = (dayHours || []).reduce(
    (sum, hour) => sum + (parseFloat(hour?.time) || 0),
    0
  );

  const plannedHoursToday = visibleEvents.reduce(
    (sum, event) => sum + getPlannedHours(event, selectedDate),
    0
  );

  const reportCount = visibleEvents.filter((event) => event.module === "report").length;
  const workOrderCount = visibleEvents.filter((event) => event.module === "work_order").length;

  const isPhoneroConfigured =
    currentCompany?.phonero_username?.trim() &&
    currentCompany?.phonero_password?.trim();

  const vehicle = driverState.vehicle;
  const driver = driverState.driver;
  const automileCheckinFilter = Boolean(currentCompany?.automile_ready);
  const checkInVehicleFilters = [
    { name: "active", value: true },
    { name: "available_for_driver_checkin", value: true },
    ...(automileCheckinFilter ? [{ name: "synced_with_automile", value: true }] : []),
  ];

  const formatHours = (value) => {
    if (value === null || value === undefined) return "0.00";
    const number = Number(typeof value === "string" ? value.replace(",", ".") : value);
    return Number.isNaN(number) ? "0.00" : number.toFixed(2);
  };

  const onSelectEvent = (event) => {
    const isUsed = (dayHours || []).some((hour) => {
      const sameDay = hour?.date && isSameDay(new Date(hour.date), new Date(selectedDate));
      if (!sameDay) return false;

      if (event?.module === "report" && hour?.report?.id && event?.module_id) {
        return hour.report.id === event.module_id;
      }

      if (event?.module === "work_order" && hour?.work_order?.id && event?.module_id) {
        return hour.work_order.id === event.module_id;
      }

      if (event?.project_id && hour?.project?.id) {
        return hour.project.id === event.project_id;
      }

      return false;
    });

    if (isUsed) {
      toast.error(t("already_registered_hours_for_event"));
      return;
    }

    const project = event?.module_details?.project;
    const projectId = project?.id || event?.project_id || event?.project?.id;
    const projectTitle =
      project?.displayTitle ||
      event?.project?.displayTitle ||
      event?.project_name ||
      event?.project?.name ||
      t("Project");

    const nextPrefill = {
      user_id: currentUser?.id,
      project_id: projectId,
      project: projectId ? { id: projectId, displayTitle: projectTitle } : undefined,
      date: selectedDate,
      description: event?.title || "",
    };

    if (event?.module === "report" && event?.module_id) {
      nextPrefill.report_id = event.module_id;
    }

    if (event?.module === "work_order" && event?.module_id) {
      nextPrefill.work_order_id = event.module_id;
    }

    setPrefill(nextPrefill);
    setHourFormId(null);
    setDialogOpen(true);
  };

  function reportFromEvent(event) {
    if (!event || event.module !== "report" || !event.module_id) return null;

    return {
      ...(event.module_details || {}),
      id: event.module_id,
      displayTitle: event?.module_details?.displayTitle || event.title,
      report_type_id:
        event?.module_details?.report_type_id ||
        event?.module_details?.report_type?.id ||
        event?.module_details?.reportType?.id,
    };
  }

  async function openReportGuide(event, options = {}) {
    const report = reportFromEvent(event);
    if (!report?.id) return;

    const templates = await fetchReportGuideTemplates({
      report_type_id: report.report_type_id,
      purpose: "field_report",
    });
    const availableTemplates = excludingPromangStockReportGuideTemplates(
      Array.isArray(templates) ? templates.filter((item) => item?.id) : []
    );
    if (availableTemplates.length === 0) {
      if (!options.silent) toast.error(t("Ingen rapportguide er tilgjengelig"));
      return;
    }

    if (availableTemplates.length > 1) {
      setReportGuideChooserState({
        open: true,
        report,
        templates: availableTemplates,
      });
      return;
    }

    startReportGuide(report, availableTemplates[0]);
  }

  function startReportGuide(report, template) {
    setReportGuideState({
      open: true,
      template,
      report,
      isSaving: false,
    });
  }

  function closeReportGuideChooser() {
    setReportGuideChooserState({
      open: false,
      report: null,
      templates: [],
    });
  }

  function closeReportGuide() {
    setReportGuideState({
      open: false,
      template: null,
      report: null,
      isSaving: false,
    });
  }

  async function completeReportGuide(guideRun) {
    if (!reportGuideState.report?.id) return;

    setReportGuideState((state) => ({ ...state, isSaving: true }));
    const response = await createReportGuideRun(reportGuideState.report.id, {
      report_guide_template_id: guideRun.report_guide_template_id,
      purpose: guideRun.purpose,
      answers: guideRun.answers,
      requested_from_customer: false,
    });

    if (response?.errors) {
      toast.error(Array.isArray(response.errors) ? response.errors.join(", ") : String(response.errors));
      setReportGuideState((state) => ({ ...state, isSaving: false }));
      return;
    }

    toast.success(t("Rapportguide lagret"));
    closeReportGuide();
    setRefreshTick((value) => value + 1);
  }

  const handleHourSaved = (savedHour) => {
    setLastChangedHour(savedHour);
    if (savedHour?.date) setSelectedDate(savedHour.date);
    setHourFormId(null);
    setRefreshTick((value) => value + 1);
  };

  const handleVehicleCheckin = async (vehicleId) => {
    if (!driver?.id) return;
    await driverVehicleAction({
      userId: currentUser?.id,
      vehicleId,
      action: "checkin",
    });
    setVehiclesListDialogOpen(false);
    setRefreshTick((value) => value + 1);
  };

  const handleVehicleCheckout = async () => {
    if (!driver?.id || !vehicle?.id) return;
    await driverVehicleAction({
      userId: currentUser?.id,
      vehicleId: vehicle.id,
      action: "checkout",
    });
    setRefreshTick((value) => value + 1);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                <HomeIcon className="h-4 w-4" />
                Dashboard
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                Dagens oppdrag
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-slate-600">
                Full oversikt over dagens arbeidsordre, aktiviteter og timelinjer for{" "}
                {format(new Date(`${selectedDate}T12:00:00`), "EEEE d. MMMM yyyy", {
                  locale: nb,
                })}
                .
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-2xl bg-slate-950 px-4 py-3 text-white">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-300">
                  Oppdrag
                </div>
                <div className="mt-2 text-2xl font-semibold">{visibleEvents.length}</div>
              </div>
              <div className="rounded-2xl bg-slate-100 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  Service
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">{reportCount}</div>
              </div>
              <div className="rounded-2xl bg-slate-100 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  Arbeidsordre
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {workOrderCount}
                </div>
              </div>
              <div className="rounded-2xl bg-slate-100 px-4 py-3">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                  Timer registrert
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-900">
                  {totalHoursToday.toFixed(1)}h
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 space-y-6">
          <DashboardAdminSetupAlerts />
          <DashboardStatsSection />
          <RoleOnly role="admin">
            <AccountingSummarySection />
          </RoleOnly>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start">
          <div className="space-y-6 lg:col-span-4">
            <RoleOnly role="quality">
              <SideSection
                title="Personalhåndbok"
                description="Direkte tilgang til handbok, kollegaer og registrering av hendelser."
              >
                {!handbookAckState.loading &&
                handbookAckState.total > 0 &&
                handbookAckState.pending > 0 ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-950">
                    {t("dashboard_report.handbook_read_incomplete")
                      .replace("{pending}", String(handbookAckState.pending))
                      .replace("{total}", String(handbookAckState.total))}
                  </div>
                ) : null}
                {!handbookAckState.loading &&
                handbookAckState.total > 0 &&
                handbookAckState.pending === 0 ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-900">
                    {t("employee_handbook.all_documents_acknowledged")}
                  </div>
                ) : null}
                <ActionButton
                  onClick={() => navigate("/dashboard/quality/employee-handbook")}
                  icon={BookOpenIcon}
                  title="Gå til håndbok"
                  subtitle="Rutiner, dokumentasjon og felles praksis."
                />
                <ActionButton
                  onClick={() => setColleaguesOpen(true)}
                  icon={UsersIcon}
                  title="Kollegaer"
                  subtitle="Finn ansatte og intern kontaktinformasjon."
                />
                <ActionButton
                  onClick={() => setToolsOpen(true)}
                  icon={WrenchScrewdriverIcon}
                  title="Verktøy"
                  subtitle="Se verktøy som er tildelt deg."
                />
                <ActionButton
                  onClick={() => setIncidentOpen(true)}
                  icon={PlusCircleIcon}
                  title="Registrer hendelse"
                  subtitle="Opprett HMS-hendelse eller avvik."
                />
              </SideSection>
            </RoleOnly>

            <RoleOnly role="vehicles" action="read">
              <SideSection
                title="Kjørebok"
                description="Status på innkjørt kjøretøy og hurtigtilgang til kjøretøyvalg."
              >
                <ActionButton
                  onClick={() => setVehicleDialogOpen(true)}
                  icon={TruckIcon}
                  title={
                    driverState.isLoading
                      ? "Laster kjøretøy"
                      : vehicle?.number_plate || (driver?.id ? "Ingen bil valgt" : "Ikke registrert som sjåfør")
                  }
                  subtitle={
                    vehicle?.id
                      ? `${vehicle.distance_driven_today || 0} km kjørt i dag`
                      : driver?.id
                      ? "Åpne kjørebok og velg bil"
                      : "Ingen førerprofil tilgjengelig"
                  }
                />
              </SideSection>
            </RoleOnly>

            {isPhoneroConfigured ? (
              <RoleOnly role="phone" action="read">
                <CurrentPhoneSection />
              </RoleOnly>
            ) : null}

            <RoleOnly role="admin">
              <SideSection
                title="Admin Actions"
                description="Snarvei til de viktigste administrative oppgavene."
              >
                <ActionLink
                  to="/dashboard/users"
                  icon={UserGroupIcon}
                  title="Ansatte"
                  subtitle="Administrer brukere, roller og tilgang."
                />
              </SideSection>
            </RoleOnly>
          </div>

          <div className="lg:col-span-8">
            <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
              <div className="border-b border-slate-200 px-6 py-6">
                <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
                  <div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                      <CalendarDaysIcon className="h-4 w-4" />
                      Dagens oversikt
                    </div>
                    <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                      Alle oppgaver for dagen
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Arbeidsordre, aktiviteter, prosjekter og avtaler i en samlet liste.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
                    <div className="font-semibold text-slate-900">
                      {format(new Date(`${selectedDate}T12:00:00`), "EEEE d. MMMM", {
                        locale: nb,
                      })}
                    </div>
                    <div className="mt-1">
                      {visibleEvents.length} oppdrag og {plannedHoursToday.toFixed(1)} planlagte timer
                    </div>
                  </div>
                </div>
              </div>

              {eventsState.isLoading ? (
                <div className="px-6 py-12 text-sm text-slate-500">Laster dagens oppdrag...</div>
              ) : visibleEvents.length === 0 ? (
                <div className="px-6 py-12">
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center">
                    <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-slate-700 shadow-sm">
                      <BriefcaseIcon className="h-7 w-7" />
                    </div>
                    <h3 className="mt-4 text-lg font-semibold text-slate-900">
                      Ingen oppdrag registrert i dag
                    </h3>
                    <p className="mt-2 text-sm text-slate-500">
                      Kalenderen kan fortsatt brukes for full oversikt og planlegging.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {visibleEvents.map((event) => {
                    const metaRows = getEventMetaRows(event);

                    return (
                      <div key={event.id} className="px-6 py-5 transition hover:bg-slate-50">
                        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                          <Link
                            to={eventPath(event)}
                            className={`inline-flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-2xl ${getEventColor(
                              event.module
                            )}`}
                          >
                            {getEventIcon(event.module)}
                          </Link>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                {moduleLabels[event.module] || event.module}
                              </span>
                              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
                                {getEventTimeLabel(event, selectedDate)}
                              </span>
                            </div>

                            <div className="mt-2 flex flex-wrap items-end gap-x-2 gap-y-2">
                              <Link
                                to={eventPath(event)}
                                className="min-w-0 flex-1 text-lg font-semibold text-slate-950 transition hover:text-slate-700"
                              >
                                {event?.module_details?.displayTitle || event.title}
                              </Link>
                              <EventStatusChips event={event} />
                            </div>

                            {event?.description || event?.module_details?.description ? (
                              <div className="mt-2 text-sm leading-6 text-slate-600 [&_a]:text-slate-900 [&_a]:underline [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:m-0 [&_ul]:list-disc [&_ul]:pl-5">
                                {parse(event?.module_details?.description || event?.description || "")}
                              </div>
                            ) : null}

                            {metaRows.length > 0 ? (
                              <div className="mt-3 flex flex-wrap gap-2">
                                {metaRows.map((item) => (
                                  <span
                                    key={`${event.id}-${item}`}
                                    className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600"
                                  >
                                    {item}
                                  </span>
                                ))}
                              </div>
                            ) : null}
                          </div>

                          <div className="flex w-full flex-col gap-3 lg:w-56">
                            <div className="rounded-2xl bg-slate-50 px-4 py-3">
                              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                                <CalendarDaysIcon className="h-4 w-4" />
                                {t("Planned time")}
                              </div>
                              <div className="mt-2 text-sm font-semibold text-slate-900">
                                {getPlannedHours(event, selectedDate).toFixed(1)} t
                              </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-2">
                              <ReportHoursTag event={event} className="min-w-0 flex-1" />
                              <ItemListProductStatusTag event={event} />
                              {event.module === "report" ? (
                                <button
                                  type="button"
                                  onClick={() => openReportGuide(event)}
                                  className="inline-flex h-11 items-center justify-center rounded-full border border-sky-300 px-4 text-sm font-semibold text-sky-700 transition hover:border-sky-400 hover:bg-sky-50"
                                >
                                  Guide
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => onSelectEvent(event)}
                                className="inline-flex h-11 items-center justify-center rounded-full border border-slate-300 px-4 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-white"
                              >
                                Timer
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="border-t border-slate-200 px-6 py-5">
                <Link
                  to={`/dashboard/calendar?view=day&date=${selectedDate}`}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 transition hover:text-slate-700"
                >
                  Se kalender
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </section>

            <RoleOnly role="hours">
              <section className="mt-6 rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-500">
                      Timeregistrering
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      Registrer og juster timer for dagens arbeid.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      const date = todayString();
                      setPrefill((prev) => ({
                        ...(prev || {}),
                        user_id: currentUser?.id,
                        date,
                      }));
                      setSelectedDate(date);
                      setDialogOpen(true);
                    }}
                    className="inline-flex items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    Åpne timeregistrering
                  </button>
                </div>

                <div className="mt-5 rounded-2xl bg-slate-50 px-4 py-4">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <div className="text-xs uppercase tracking-[0.18em] text-slate-500">
                        Registrert i dag
                      </div>
                      <div className="mt-2 text-3xl font-semibold text-slate-950">
                        {totalHoursToday.toFixed(1)}h
                      </div>
                    </div>
                    <div className="text-right text-sm text-slate-500">
                      <div>{(dayHours || []).length} linjer</div>
                      <div>{plannedHoursToday.toFixed(1)}h planlagt</div>
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    {(dayHours || []).length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-5 text-sm text-slate-500">
                        Ingen timer registrert ennå.
                      </div>
                    ) : (
                      dayHours.map((hour) => (
                        <button
                          type="button"
                          key={hour.id}
                          onClick={() => {
                            setHourFormId(hour.id);
                            setPrefill(null);
                            setDialogOpen(true);
                          }}
                          className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
                        >
                          <div className="min-w-0">
                            <div className="truncate text-sm font-semibold text-slate-900">
                              {hour.description || t("Time Registration")}
                            </div>
                            <div className="truncate text-xs text-slate-500">
                              {hour.project?.displayTitle ||
                                hour.activity?.displayTitle ||
                                "Uten prosjekt"}
                            </div>
                          </div>
                          <div className="ml-4 whitespace-nowrap text-sm font-semibold text-slate-900">
                            {formatHours(hour.time)}h
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </section>
            </RoleOnly>
          </div>
        </div>

        <PMDialog
          open={dialogOpen}
          onClose={() => {
            setDialogOpen(false);
            setPrefill(null);
            setHourFormId(null);
          }}
          title={t("Time Registration")}
          size="5xl"
          dense
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain px-2 sm:px-0">
              <HourForm
                showQuickHourSelection={true}
                hourId={hourFormId}
                onChange={handleHourSaved}
                prefill={prefill}
                onDateChange={(date) => {
                  setSelectedDate(date);
                  setRefreshTick((value) => value + 1);
                }}
                onClose={() => {
                  setDialogOpen(false);
                  setPrefill(null);
                  setHourFormId(null);
                  setRefreshTick((value) => value + 1);
                }}
                renderAfterDateTime={() => {
                  if (!selectedDate || eventsState.isLoading) return null;
                  if (visibleEvents.length === 0) return null;

                  return (
                    <div className="mt-2">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Dagens oppdrag
                      </div>
                      <div className="flex gap-2 overflow-x-auto py-1">
                        {visibleEvents.map((event) => (
                          <button
                            key={event.id}
                            type="button"
                            onClick={() => onSelectEvent(event)}
                            className="shrink-0 rounded-2xl border border-slate-200 bg-white px-3 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
                          >
                            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                              {moduleLabels[event.module] || event.module}
                            </div>
                            <div className="mt-1 max-w-[220px] truncate text-sm font-semibold text-slate-900">
                              {event?.module_details?.displayTitle || event.title}
                            </div>
                            <div className="mt-1 text-xs text-slate-500">
                              {getEventTimeLabel(event, selectedDate)}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }}
                renderAfterDescription={() => (
                  <div className="mt-4">
                    {(dayHours || []).length > 0 ? (
                      <>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                          Registrerte timer
                        </div>
                        <div className="space-y-2">
                          {dayHours.map((hour) => (
                            <button
                              type="button"
                              key={hour.id}
                              onClick={() => {
                                setHourFormId(hour.id);
                                setPrefill(null);
                              }}
                              className="flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left transition hover:border-slate-300 hover:bg-slate-50"
                            >
                              <div className="min-w-0">
                                <div className="truncate text-sm font-semibold text-slate-900">
                                  {hour.description || t("Time Registration")}
                                </div>
                                <div className="truncate text-xs text-slate-500">
                                  {hour.project?.displayTitle ||
                                    hour.activity?.displayTitle ||
                                    "Uten prosjekt"}
                                </div>
                              </div>
                              <div className="ml-3 text-xs font-semibold text-slate-900">
                                {formatHours(hour.time)}h
                              </div>
                            </button>
                          ))}
                        </div>
                      </>
                    ) : null}
                  </div>
                )}
              />
            </div>
          </div>
        </PMDialog>

        <PMDialog
          open={reportGuideChooserState.open}
          onClose={closeReportGuideChooser}
          title={t("Velg rapportguide")}
          size="3xl"
        >
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              {t("Velg guiden som passer best med oppdraget.")}
            </p>
            {(reportGuideChooserState.templates || []).map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => {
                  const report = reportGuideChooserState.report;
                  closeReportGuideChooser();
                  startReportGuide(report, template);
                }}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-4 text-left transition hover:border-sky-300 hover:bg-sky-50"
              >
                <div className="text-sm font-semibold text-slate-950">
                  {template.title || t("Rapportguide")}
                </div>
                {template.description ? (
                  <div className="mt-1 text-sm text-slate-500">{template.description}</div>
                ) : null}
                <div className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                  {(template.questions || []).length} {t("spørsmål")}
                </div>
              </button>
            ))}
          </div>
        </PMDialog>

        <ReportGuideOnboardingDialog
          open={reportGuideState.open}
          template={reportGuideState.template}
          report={reportGuideState.report}
          isSaving={reportGuideState.isSaving}
          onClose={closeReportGuide}
          onComplete={completeReportGuide}
        />

        <PMDialog
          open={colleaguesOpen}
          onClose={() => setColleaguesOpen(false)}
          title="Kollegaer"
          size="3xl"
        >
          <div className="space-y-4">
            <div className="rounded-2xl bg-slate-50 px-4 py-4 text-sm text-slate-600">
              Ansatte og intern kontaktinformasjon.
            </div>
            {colleaguesState.isLoading ? (
              <div className="text-sm text-slate-500">Laster kollegaer...</div>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {colleaguesState.data.map((user) => {
                  const nextOfKin = Array.isArray(user.user_next_of_kins)
                    ? user.user_next_of_kins[0]
                    : null;
                  const phoneNumber = user.mobile_phone || user.phone;

                  return (
                    <div
                      key={user.id}
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-4"
                    >
                      <div className="text-sm font-semibold text-slate-900">
                        {user.displayTitle || user.name}
                      </div>
                      <div className="mt-3 space-y-1 text-xs text-slate-600">
                        <div>E-post: {user.email || "Ikke registrert"}</div>
                        <div>Telefon: {phoneNumber || "Ikke registrert"}</div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        {phoneNumber ? (
                          <a
                            href={`tel:${phoneNumber}`}
                            className="inline-flex items-center rounded-full border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                          >
                            Ring
                          </a>
                        ) : null}
                        {user.email ? (
                          <a
                            href={`mailto:${user.email}`}
                            className="inline-flex items-center rounded-full bg-slate-950 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800"
                          >
                            E-post
                          </a>
                        ) : null}
                      </div>

                      <div className="mt-4 rounded-xl bg-slate-50 px-3 py-3">
                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                          {"P\u00e5r\u00f8rende"}
                        </div>
                        {nextOfKin ? (
                          <div className="mt-2 space-y-1 text-xs text-slate-600">
                            <div>Navn: {nextOfKin.name || "Ikke registrert"}</div>
                            <div>
                              Relasjon: {nextOfKin.relationship_type || "Ikke registrert"}
                            </div>
                            <div>
                              Telefon: {nextOfKin.phone_number || "Ikke registrert"}
                            </div>
                            {nextOfKin.address ? <div>Adresse: {nextOfKin.address}</div> : null}
                          </div>
                        ) : (
                          <div className="mt-2 text-xs text-slate-500">
                            {"Ingen p\u00e5r\u00f8rende registrert"}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </PMDialog>

        <PMDialog
          open={incidentOpen}
          onClose={() => setIncidentOpen(false)}
          title="Registrer hendelse"
          size="5xl"
        >
          <HseEventForm
            onSubmit={() => {
              setIncidentOpen(false);
              toast.success("Hendelse opprettet");
            }}
            onCancel={() => setIncidentOpen(false)}
          />
        </PMDialog>

        <EmployeeToolsDialog
          open={toolsOpen}
          onClose={() => setToolsOpen(false)}
          userId={currentUser?.id}
        />

        <PMDialog
          open={vehicleDialogOpen}
          onClose={() => setVehicleDialogOpen(false)}
          title="Kjørebok"
          size="3xl"
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  I dag
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-950">
                  {vehicle?.distance_driven_today || 0} km
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Denne uken
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-950">
                  {vehicle?.distance_driven_this_week || 0} km
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 px-4 py-4">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Denne måneden
                </div>
                <div className="mt-2 text-2xl font-semibold text-slate-950">
                  {vehicle?.distance_driven_this_month || 0} km
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-4">
              <div className="text-sm font-semibold text-slate-900">
                {vehicle?.number_plate || "Ingen bil valgt"}
              </div>
              <div className="mt-1 text-sm text-slate-500">
                {driver?.id
                  ? vehicle?.id
                    ? "Du er innlogget på kjøretøyet under."
                    : "Velg et kjøretøy for å starte registrering."
                  : "Ingen førerprofil tilgjengelig."}
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              {vehicle?.id ? (
                <>
                  <button
                    type="button"
                    onClick={handleVehicleCheckout}
                    className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800"
                  >
                    <ArrowRightOnRectangleIcon className="h-4 w-4" />
                    Sjekk ut
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setVehicleDialogOpen(false);
                      navigate(`/dashboard/driving/vehicles/${vehicle.id}/handbook`);
                    }}
                    className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                  >
                    <BookOpenIcon className="h-4 w-4" />
                    Åpne kjørebok
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setVehiclesListDialogOpen(true)}
                  disabled={!driver?.id}
                  className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <ArrowLeftOnRectangleIcon className="h-4 w-4" />
                  Velg kjøretøy
                </button>
              )}
            </div>
          </div>
        </PMDialog>

        <VehiclesListDialog
          open={vehiclesListDialogOpen}
          onClose={() => setVehiclesListDialogOpen(false)}
          checkInMode
          filters={checkInVehicleFilters}
          onSelect={handleVehicleCheckin}
        />
      </div>
    </div>
  );
};
